// WhatsAppService.js - Handles sending user responses to WhatsApp
class WhatsAppService {
    constructor() {
        // Australian WhatsApp number (replace with your actual number)
        this.WHATSAPP_NUMBER = '+61424833071'; // Replace with actual Australian number
        this.API_BASE_URL = 'https://api.whatsapp.com/send';
    }

    // Format user data and session responses for WhatsApp
    formatUserReport(userData, sessionData, currentPathway) {
        const timestamp = new Date().toLocaleString('en-AU', {
            timeZone: 'Australia/Sydney',
            dateStyle: 'full',
            timeStyle: 'short'
        });

        let report = `🏥 *NEW PATIENT ASSESSMENT* 🏥\n\n`;
        report += `📅 *Date:* ${timestamp}\n`;
        report += `👤 *Name:* ${userData.name}\n`;
        report += `📱 *Phone:* ${userData.phone}\n`;
        
        if (userData.email) {
            report += `📧 *Email:* ${userData.email}\n`;
        }
        
        report += `🎯 *Primary Goal:* ${this.getGoalDescription(currentPathway)}\n\n`;

        // Core Assessment Data
        report += `📊 *CORE ASSESSMENT*\n`;
        report += `━━━━━━━━━━━━━━━━━━━━\n`;
        
        if (sessionData.healthRating) {
            report += `🏥 Health Rating: ${sessionData.healthRating}/10\n`;
        }
        
        if (sessionData.medicalConditions) {
            report += `💊 Medical Conditions: ${sessionData.medicalConditions}\n`;
        }
        
        if (sessionData.fallHistory) {
            report += `⚠️ Fall History: ${sessionData.fallHistory}\n`;
        }
        
        if (sessionData.fallDetails) {
            report += `📝 Fall Details: ${sessionData.fallDetails}\n`;
        }
        
        if (sessionData.fallWorry) {
            report += `😰 Fall Worry: ${sessionData.fallWorry}\n`;
        }
        
        if (sessionData.walkingAbility) {
            report += `🚶 Walking Ability: ${sessionData.walkingAbility}\n`;
        }
        
        if (sessionData.mobilityAid) {
            report += `🦯 Uses Mobility Aid: ${sessionData.mobilityAid}\n`;
        }
        
        if (sessionData.aidType) {
            report += `🛠️ Aid Type: ${sessionData.aidType}\n`;
        }
        
        if (sessionData.exerciseComfort) {
            report += `💪 Exercise Comfort: ${sessionData.exerciseComfort}\n`;
        }

        // Pathway-specific data
        report += `\n🎯 *${this.getGoalDescription(currentPathway).toUpperCase()} PATHWAY*\n`;
        report += `━━━━━━━━━━━━━━━━━━━━\n`;
        
        report += this.formatPathwayData(currentPathway, sessionData);

        // Footer
        report += `\n⏰ *Action Required:* Contact patient within 48 hours\n`;
        report += `🔗 *System:* H.A.M.A. Assessment Bot\n`;
        
        return report;
    }

    // Get goal description
    getGoalDescription(pathway) {
        const pathways = {
            'wellness': 'Wellness & Healthy Aging',
            'pain': 'Chronic Pain Management',
            'health': 'Health Condition Management',
            'rehab': 'Rehabilitation Program',
            'exploring': 'General Information'
        };
        return pathways[pathway] || 'General Assessment';
    }

    // Format pathway-specific data
    formatPathwayData(pathway, sessionData) {
        let pathwayReport = '';

        switch (pathway) {
            case 'wellness':
                if (sessionData.wellness_activity_level) {
                    pathwayReport += `🏃 Activity Level: ${sessionData.wellness_activity_level}\n`;
                }
                if (sessionData.wellness_physical_tasks) {
                    pathwayReport += `💪 Physical Tasks: ${sessionData.wellness_physical_tasks}\n`;
                }
                if (sessionData.wellness_daily_activities) {
                    pathwayReport += `🏠 Daily Activities: ${sessionData.wellness_daily_activities}\n`;
                }
                if (sessionData.wellness_discomfort) {
                    pathwayReport += `😣 Experiences Discomfort: ${sessionData.wellness_discomfort}\n`;
                }
                if (sessionData.wellness_discomfort_location) {
                    pathwayReport += `📍 Discomfort Location: ${sessionData.wellness_discomfort_location}\n`;
                }
                if (sessionData.wellness_discomfort_rating) {
                    pathwayReport += `🎚️ Discomfort Rating: ${sessionData.wellness_discomfort_rating}/10\n`;
                }
                if (sessionData.wellness_sleep) {
                    pathwayReport += `😴 Sleep Quality: ${sessionData.wellness_sleep}\n`;
                }
                if (sessionData.wellness_activity_goal) {
                    pathwayReport += `🎯 Activity Goals: ${sessionData.wellness_activity_goal}\n`;
                }
                if (sessionData.wellness_meaning) {
                    pathwayReport += `💭 Healthy Aging Meaning: ${sessionData.wellness_meaning}\n`;
                }
                break;

            case 'pain':
                if (sessionData.pain_location) {
                    pathwayReport += `📍 Pain Location: ${sessionData.pain_location}\n`;
                }
                if (sessionData.pain_current_level) {
                    pathwayReport += `🎚️ Current Pain Level: ${sessionData.pain_current_level}/10\n`;
                }
                if (sessionData.pain_description) {
                    pathwayReport += `📝 Pain Description: ${sessionData.pain_description}\n`;
                }
                if (sessionData.pain_triggers) {
                    pathwayReport += `⚡ Pain Triggers: ${sessionData.pain_triggers}\n`;
                }
                if (sessionData.pain_relief) {
                    pathwayReport += `💊 Pain Relief Methods: ${sessionData.pain_relief}\n`;
                }
                if (sessionData.pain_daily_impact) {
                    pathwayReport += `📅 Daily Impact: ${sessionData.pain_daily_impact}\n`;
                }
                break;

            case 'health':
                if (sessionData.health_condition_type) {
                    pathwayReport += `🏥 Condition Type: ${sessionData.health_condition_type}\n`;
                }
                if (sessionData.health_specific_condition) {
                    pathwayReport += `📋 Specific Condition: ${sessionData.health_specific_condition}\n`;
                }
                if (sessionData.health_symptoms) {
                    pathwayReport += `🩺 Symptoms: ${sessionData.health_symptoms}\n`;
                }
                if (sessionData.health_specialist) {
                    pathwayReport += `👨‍⚕️ Seeing Specialist: ${sessionData.health_specialist}\n`;
                }
                if (sessionData.health_clearance) {
                    pathwayReport += `✅ Exercise Clearance: ${sessionData.health_clearance}\n`;
                }
                break;

            case 'rehab':
                if (sessionData.rehab_reason) {
                    pathwayReport += `🏥 Rehab Reason: ${sessionData.rehab_reason}\n`;
                }
                if (sessionData.rehab_specific_details) {
                    pathwayReport += `📝 Specific Details: ${sessionData.rehab_specific_details}\n`;
                }
                if (sessionData.rehab_clearance) {
                    pathwayReport += `✅ Medical Clearance: ${sessionData.rehab_clearance}\n`;
                }
                if (sessionData.rehab_goal) {
                    pathwayReport += `🎯 Rehab Goal: ${sessionData.rehab_goal}\n`;
                }
                break;

            default:
                pathwayReport += `No specific pathway data collected.\n`;
        }

        return pathwayReport || 'No additional pathway data collected.\n';
    }

    // Send report via WhatsApp Web (opens in new tab)
    sendViaWhatsAppWeb(userData, sessionData, currentPathway) {
        try {
            const message = this.formatUserReport(userData, sessionData, currentPathway);
            const encodedMessage = encodeURIComponent(message);
            const whatsappUrl = `${this.API_BASE_URL}?phone=${this.WHATSAPP_NUMBER}&text=${encodedMessage}`;
            
            // Open WhatsApp Web in new tab
            window.open(whatsappUrl, '_blank');
            
            return {
                success: true,
                message: 'WhatsApp message prepared successfully'
            };
        } catch (error) {
            console.error('WhatsApp integration error:', error);
            return {
                success: false,
                message: 'Failed to prepare WhatsApp message'
            };
        }
    }

    // Alternative: Use WhatsApp Business API (requires API key and webhook)
    async sendViaWhatsAppAPI(userData, sessionData, currentPathway) {
        try {
            const message = this.formatUserReport(userData, sessionData, currentPathway);
            
            // This would require a WhatsApp Business API endpoint
            // For now, we'll simulate the API call
            console.log('Sending to WhatsApp API:', {
                to: this.WHATSAPP_NUMBER,
                message: message
            });

            // In a real implementation, you would make an API call here:
            /*
            const response = await fetch('YOUR_WHATSAPP_API_ENDPOINT', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer YOUR_API_TOKEN'
                },
                body: JSON.stringify({
                    messaging_product: 'whatsapp',
                    to: this.WHATSAPP_NUMBER.replace('+', ''),
                    type: 'text',
                    text: { body: message }
                })
            });
            */

            return {
                success: true,
                message: 'Assessment sent to physiotherapy team via WhatsApp'
            };
        } catch (error) {
            console.error('WhatsApp API error:', error);
            return {
                success: false,
                message: 'Failed to send via WhatsApp API'
            };
        }
    }

    // Main method to send assessment
    async sendAssessment(userData, sessionData, currentPathway, method = 'web') {
        if (method === 'api') {
            return await this.sendViaWhatsAppAPI(userData, sessionData, currentPathway);
        } else {
            return this.sendViaWhatsAppWeb(userData, sessionData, currentPathway);
        }
    }

    // Update WhatsApp number (for configuration)
    updateWhatsAppNumber(newNumber) {
        // Validate Australian number format
        const australianNumberRegex = /^\+61[0-9]{9}$/;
        if (!australianNumberRegex.test(newNumber)) {
            throw new Error('Please provide a valid Australian phone number in format +61XXXXXXXXX');
        }
        this.WHATSAPP_NUMBER = newNumber;
    }

    // Get current WhatsApp number
    getWhatsAppNumber() {
        return this.WHATSAPP_NUMBER;
    }
}

// Create singleton instance
const whatsAppService = new WhatsAppService();

export default whatsAppService;